/**
 * AttributeField Component
 *
 * Renders a single attribute input field with validation
 * Handles both simple text/number inputs and site/batch selectors
 */

import React, { useEffect, useState, useImperativeHandle, forwardRef } from "react"
import { Input, VStack, Text, HStack, Box, Textarea, Link as TriggerLink } from "@chakra-ui/react"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { FaLink } from "react-icons/fa6"

import { Field } from "@hydrologiq/shared/chakra/Field"
import Popover from "@hydrologiq/shared/chakra/Popover"
import type { AttributeConfig, ActivityContext, ValidationResult } from "@hydrologiq/shared/utils/attribute-rules"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import SitesCombobox, { UnifiedSiteItem } from "../inputs/SitesCombobox"
import BatchCombobox from "../inputs/BatchCombobox"
import AssetCombobox, { AssetItem } from "../inputs/AssetCombobox"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"

export interface LinkedActivityInfo {
  activityName: string
  activityKind: string
  isInherited: boolean
}

export interface AttributeFieldRef {
  validate: () => ValidationResult | null
}

export interface AttributeFieldProps {
  /** Attribute configuration from rule engine */
  config: AttributeConfig

  /** Current activity context */
  context: ActivityContext

  /** Current field value */
  value: string

  /** Called when value changes */
  onChange: (value: string) => void

  /** Called when user wants to create a new batch */
  onNavigateToBatchCreation?: () => void

  /** Newly created batch to populate in the dropdown */
  newlyCreatedBatch?: { id: string; name: string } | null

  /** Information about linked activity if this attribute is inherited */
  linkedActivityInfo?: LinkedActivityInfo
}

/**
 * Basic attribute input field
 *
 * Supports text, number, and select inputs based on attribute type
 * Validates input and shows errors/warnings
 */
export const AttributeField = forwardRef<AttributeFieldRef, AttributeFieldProps>(
  ({ config, context, value, onChange, onNavigateToBatchCreation, newlyCreatedBatch, linkedActivityInfo }, ref) => {
    const [validationResult, setValidationResult] = useState<ValidationResult | null>(null)
    const [hasChanged, setHasChanged] = useState(false)

    const copy = useCopyValues({
      [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
    })

    // Track the current value to prevent onChange calls during remount
    const currentValueRef = React.useRef(value)
    currentValueRef.current = value

    // Helper to render linked activity indicator
    const renderLinkedIndicator = () => {
      if (!linkedActivityInfo?.isInherited || linkedActivityInfo.activityKind !== gqlTypes.ActivityKind.Movement)
        return null

      return (
        <HStack gap={1} fontSize="xs" color="fg.muted">
          <FaLink size={10} />
          <Text>From {linkedActivityInfo.activityName}</Text>
        </HStack>
      )
    }

    // Ref to store BatchCombobox methods
    const batchComboboxMethodsRef = React.useRef<{ selectBatch: (batch: { id: string; name: string }) => void } | null>(
      null
    )

    // Apply default value on mount
    // This runs every time the component mounts (including remounts due to key changes)
    useEffect(() => {
      if (config.defaultValue) {
        const defaultVal = config.defaultValue(context)

        // For site/batch fields: empty string "" means explicitly cleared — don't override
        // For other fields: empty string "" means no value yet — apply default
        const isSiteOrBatchField = [
          gqlTypes.ActivityAttributeKind.Site,
          gqlTypes.ActivityAttributeKind.Source,
          gqlTypes.ActivityAttributeKind.Destination,
          gqlTypes.ActivityAttributeKind.Batch,
        ].includes(config.attribute as gqlTypes.ActivityAttributeKind)

        // For site/batch fields, we want to apply default only if value is null/undefined (not empty string)
        const shouldApplyDefault = isSiteOrBatchField
          ? value === null || value === undefined
          : !value

        if (shouldApplyDefault) {
          if (defaultVal) {
            onChange(defaultVal)
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Run ONLY on mount - never re-apply defaults after user interaction

    // Validate on value change
    // Only re-validate when value or validator changes, not on every context change
    useEffect(() => {
      if (config.validator && hasChanged) {
        const result = config.validator(value, context)
        setValidationResult(result)
      } else {
        setValidationResult(null)
      }
    }, [value, config.validator]) // Removed context from dependencies to prevent re-validation on every render

    // Expose validation function through ref
    useImperativeHandle(ref, () => ({
      validate: () => {
        // Check required fields first
        if (config.required && (!value || value.trim() === "")) {
          const result = {
            valid: false,
            error: `${config.label || config.attribute} is required`,
          }
          setValidationResult(result)
          setHasChanged(true)
          return result
        }

        // Then run custom validator if defined
        if (config.validator) {
          const result = config.validator(value, context)
          setValidationResult(result)
          setHasChanged(true) // Mark as changed so validation displays
          return result
        }
        return null
      },
    }))

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(e.target.value)
      setHasChanged(true)
    }

    const handleSiteChange = (site: UnifiedSiteItem | undefined) => {
      const newValue = site?.id || ""
      // Only call onChange if the value actually changed from the current tracked value
      if (newValue !== currentValueRef.current) {
        currentValueRef.current = newValue
        onChange(newValue)
      }
    }

    const handleBatchChange = (batch: { id: string; name: string } | undefined) => {
      const newValue = batch?.id || ""
      // Only call onChange if the value actually changed from the current tracked value
      if (newValue !== currentValueRef.current) {
        currentValueRef.current = newValue
        onChange(newValue)
      }
    }

    const handleAssetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = e.target.value || ""
      if (newValue !== currentValueRef.current) {
        currentValueRef.current = newValue
        onChange(newValue)
      }
    }

    const handleAssetComboboxChange = (item: AssetItem | undefined) => {
      const newValue = item?.id || ""
      if (newValue !== currentValueRef.current) {
        currentValueRef.current = newValue
        onChange(newValue)
      }
    }

    // Update ref when value prop changes
    React.useEffect(() => {
      currentValueRef.current = value
    }, [value])

    // Evaluate editable - can be boolean or function
    const isEditable = typeof config.editable === "function" ? config.editable(context) : config.editable
    const isDisabled = !isEditable

    // Determine input type based on attribute
    const inputType = getInputType(config.attribute)

    useEffect(() => {
      if (
        context.availableAssets &&
        context.availableAssets.length > 0 &&
        config.attribute === "asset" &&
        !context.allowAnyAsset
      ) {
        handleAssetChange({ target: { value: context.availableAssets[0].id } } as React.ChangeEvent<HTMLSelectElement>)
      }
    }, [context.availableAssets, context.allowAnyAsset])
    // Render Asset dropdown or combobox
    if (config.attribute === "asset") {
      const orderAssets = context.availableAssets
        ? context.availableAssets
        : context.asset
        ? [context.asset]
        : context.order?.assets ?? []
      const selectedAssetId = value

      // If allowAnyAsset is true, render AssetCombobox for selecting any asset
      if (context.allowAnyAsset) {
        return (
          <Field
            label={
              config.helpText ? (
                <HStack gap={1}>
                  <Text>{config.label || "Asset"}</Text>
                  <Popover
                    placement="top"
                    iconType="info"
                    ariaLabel={`${config.label || "Asset"} information`}
                    size="xs"
                  >
                    <Text fontSize="sm" p={2} maxW="300px">
                      {config.helpText}
                    </Text>
                  </Popover>
                </HStack>
              ) : (
                config.label || "Asset"
              )
            }
            required={config.required}
            invalid={validationResult?.valid === false}
            errorText={validationResult?.error}
          >
            <VStack align="stretch" gap={1}>
              <AssetCombobox
                onItemSelection={handleAssetComboboxChange}
                valueId={selectedAssetId || null}
                disabled={isDisabled}
                required={config.required}
              />
              {validationResult?.warning && (
                <Text fontSize="xs" color="orange.500">
                  ⚠️ {validationResult.warning}
                </Text>
              )}
            </VStack>
          </Field>
        )
      }

      // Default: render NativeSelectField with limited assets
      return (
        <Field
          label={
            config.helpText ? (
              <HStack gap={1}>
                <Text>{config.label || "Asset"}</Text>
                <Popover placement="top" iconType="info" ariaLabel={`${config.label || "Asset"} information`} size="xs">
                  <Text fontSize="sm" p={2} maxW="300px">
                    {config.helpText}
                  </Text>
                </Popover>
              </HStack>
            ) : (
              config.label || "Asset"
            )
          }
          required={config.required}
          invalid={validationResult?.valid === false}
          errorText={validationResult?.error}
        >
          <VStack align="stretch" gap={1} width="180px" height={"100%"}>
            <Box opacity={isDisabled ? 0.6 : 1} pointerEvents={isDisabled ? "none" : "auto"}>
              <NativeSelectRoot>
                <NativeSelectField name="asset" value={selectedAssetId} onChange={handleAssetChange}>
                  {orderAssets.length === 0 && <option value="">No assets available</option>}
                  {orderAssets.length > 0 && !selectedAssetId && <option value="">Select an asset</option>}
                  {orderAssets.map((asset) => (
                    <option value={asset.id} key={asset.id}>
                      {asset.name}
                    </option>
                  ))}
                </NativeSelectField>
              </NativeSelectRoot>
            </Box>
            {validationResult?.warning && (
              <Text fontSize="xs" color="orange.500">
                ⚠️ {validationResult.warning}
              </Text>
            )}
          </VStack>
        </Field>
      )
    }

    // Render Site combobox
    if (
      [
        gqlTypes.ActivityAttributeKind.Site,
        gqlTypes.ActivityAttributeKind.Source,
        gqlTypes.ActivityAttributeKind.Destination,
      ].includes(config.attribute)
    ) {
      let initialSite: UnifiedSiteItem | undefined = undefined

      // Prioritize current value prop over previous attributes
      if (value) {
        // First check previous attributes (most reliable source)
        const previousSiteAttr = context.activity.attributes?.find(
          (attr: gqlTypes.ActivityAttribute) =>
            [
              gqlTypes.ActivityAttributeKind.Site,
              gqlTypes.ActivityAttributeKind.Source,
              gqlTypes.ActivityAttributeKind.Destination,
            ].includes(attr.attribute) && attr.site?.id === value
        )

        if (previousSiteAttr?.site) {
          // Site from attributes - SiteWithLocation (id, name, location only)
          // We need to infer the type based on activity kind since SiteWithLocation doesn't have type fields
          const siteData = previousSiteAttr.site

          initialSite = {
            id: siteData.id,
            name: siteData.name,
            types: context.activity.kind === "filling" ? ["filling"] : ["customer"],
            is_customer_site: context.activity.kind !== "filling",
            is_filling_site: context.activity.kind === "filling",
          }
        } else {
          // Fallback: Try to find the site in order's customer sites
          // Note: For filling activities, this should typically not match since filling sites aren't in order.sites
          const orderSites = context.order?.sites ?? []
          const matchingCustomerSite = orderSites.find((cs) => cs.site?.id === value)

          if (matchingCustomerSite?.site) {
            // Customer site from order - always a customer site
            const site = matchingCustomerSite.site
            initialSite = {
              id: site.id,
              name: site.name,
              types: ["customer"],
              is_customer_site: true,
              is_filling_site: false,
            }
          }
        }
      }

      // Determine site types filter based on activity kind
      const siteTypes: string[] | undefined =
        context.activity.kind === gqlTypes.ActivityKind.Filling ? ["filling"] : undefined

      // Create a stable key that changes when site type filter changes or value changes
      // This forces the combobox to remount with fresh data when switching between filling/non-filling
      // or when the value is programmatically set (e.g., from activity linking)
      const comboboxKey = `site-${context.activity.kind}-${value || "empty"}`

      // Determine the value to pass to SitesCombobox:
      // - If we have initialSite object, pass it (site is known)
      // - If we have a value but no initialSite, pass undefined (let valueId resolve it)
      // - If we have no value, pass null (explicitly clear the combobox)
      const comboboxValue = initialSite ? initialSite : value ? undefined : null

      let label = config.label || "Site"
      if (label.toLowerCase() === "site") {
        label = copy[COPY_KEYS.HEADINGS.SITE]
      }
      return (
        <Field
          label={
            config.helpText ? (
              <HStack gap={1}>
                <Text>{label}</Text>
                <Popover placement="top" iconType="info" ariaLabel={`${label} information`} size="xs">
                  <Text fontSize="sm" p={2} maxW="300px">
                    {config.helpText}
                  </Text>
                </Popover>
              </HStack>
            ) : (
              label
            )
          }
          required={config.required}
          invalid={validationResult?.valid === false}
          errorText={validationResult?.error}
        >
          <VStack align="stretch" gap={1}>
            <SitesCombobox
              key={comboboxKey}
              onItemSelection={handleSiteChange}
              value={comboboxValue}
              valueId={value || undefined}
              disabled={isDisabled}
              placeholder={isDisabled ? "Not applicable" : undefined}
              required={config.required}
              siteTypes={siteTypes}
            />
            {validationResult?.warning && (
              <Text fontSize="xs" color="orange.500">
                ⚠️ {validationResult.warning}
              </Text>
            )}
            {renderLinkedIndicator()}
          </VStack>
        </Field>
      )
    }

    // Render Batch combobox
    if (config.attribute === gqlTypes.ActivityAttributeKind.Batch) {
      console.log("[AttributeField] Rendering Batch combobox:", {
        value,
        activityKind: context.activity.kind,
        eventKind: context.eventKind,
        activityAttributesCount: context.activity.attributes?.length,
        orderBatchesCount: context.order?.batches?.length,
        newlyCreatedBatch,
      })

      // BatchCombobox expects initialValue as string ID
      const batchId = value || undefined

      // Handle newly created batch - populate it in the dropdown when available
      React.useEffect(() => {
        if (newlyCreatedBatch && batchComboboxMethodsRef.current) {
          console.log("[AttributeField] Populating newly created batch:", newlyCreatedBatch)
          batchComboboxMethodsRef.current.selectBatch(newlyCreatedBatch)
        }
      }, [newlyCreatedBatch])

      return (
        <Field
          label={
            config.helpText ? (
              <HStack gap={1}>
                <Text>{config.label || "Batch"}</Text>
                <Popover placement="top" iconType="info" ariaLabel={`${config.label || "Batch"} information`} size="xs">
                  <Text fontSize="sm" p={2} maxW="300px">
                    {config.helpText}
                  </Text>
                </Popover>
              </HStack>
            ) : (
              config.label || "Batch"
            )
          }
          required={config.required}
          invalid={validationResult?.valid === false}
          errorText={validationResult?.error}
        >
          <VStack align="stretch" gap={1}>
            <BatchCombobox
              onItemSelection={handleBatchChange}
              value={batchId || null}
              disabled={!isEditable}
              layout="horizontal"
              required={config.required}
              showCreateButton={false}
              onReady={(methods) => {
                batchComboboxMethodsRef.current = methods
              }}
            />
            {!isDisabled && onNavigateToBatchCreation && (
              <TriggerLink fontSize="sm" cursor="pointer" onClick={onNavigateToBatchCreation} variant="primary">
                Create new batch
              </TriggerLink>
            )}
            {validationResult?.warning && (
              <Text fontSize="xs" color="orange.500">
                ⚠️ {validationResult.warning}
              </Text>
            )}
          </VStack>
        </Field>
      )
    }

    // Render standard input for other attributes
    return (
      <Field
        label={
          config.helpText ? (
            <HStack gap={1}>
              <Text>{config.label || config.attribute}</Text>
              <Popover
                placement="top"
                iconType="info"
                ariaLabel={`${config.label || config.attribute} information`}
                size="xs"
              >
                <Text fontSize="sm" p={2} maxW="300px">
                  {config.helpText}
                </Text>
              </Popover>
            </HStack>
          ) : (
            config.label || config.attribute
          )
        }
        required={config.required}
        invalid={validationResult?.valid === false}
        errorText={validationResult?.error}
      >
        <VStack align="stretch" gap={1}>
          {inputType === "textarea" ? (
            <Textarea
              value={value}
              onChange={handleChange}
              disabled={isDisabled}
              placeholder={isDisabled ? "Not applicable" : `Enter ${config.label?.toLowerCase() || config.attribute}`}
              height="100px"
              width="100%"
              resize="none"
            />
          ) : (
            <Input
              type={inputType}
              value={value}
              onChange={handleChange}
              disabled={isDisabled}
              placeholder={isDisabled ? "Not applicable" : `Enter ${config.label?.toLowerCase() || config.attribute}`}
              step={inputType === "number" ? "0.1" : undefined}
            />
          )}
          {validationResult?.warning && (
            <Text fontSize="xs" color="orange.500">
              ⚠️ {validationResult.warning}
            </Text>
          )}
        </VStack>
      </Field>
    )
  }
)

/**
 * Determine HTML input type based on attribute kind
 */
function getInputType(attribute: gqlTypes.ActivityAttributeKind | string): string {
  switch (attribute) {
    case gqlTypes.ActivityAttributeKind.Pressure:
    case gqlTypes.ActivityAttributeKind.Temperature:
    case gqlTypes.ActivityAttributeKind.Quantity:
    case "fill_level":
      return "number"
    case gqlTypes.ActivityAttributeKind.Note:
      return "textarea"
    default:
      return "text"
  }
}

// Export memoized version to prevent unnecessary re-renders on GraphQL mutations
export default React.memo(AttributeField)
